import type { MassUnit } from '@/utils/calculatorMath'
import CalculatorActions from '@/components/calculators/CalculatorActions'
import ValidationAlert from '@/components/calculators/ValidationAlert'
import { calculateCopyNumber, formatNumber, massToNanograms } from '@/utils/calculatorMath'
import {Lightbulb} from 'lucide-react'
import { useState } from 'react'

const MASS_UNITS: MassUnit[] = ['ng', 'pg']

function QpcrCopyNumberCalculator() {
  const [mass, setMass] = useState('')
  const [massUnit, setMassUnit] = useState<MassUnit>('ng')
  const [length, setLength] = useState('')
  const [result, setResult] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const clearFeedback = () => setCopied(false)

  const handleReset = () => {
    setMass('')
    setMassUnit('ng')
    setLength('')
    setResult(null)
    setError(null)
    setCopied(false)
  }

  const handleCalculate = () => {
    clearFeedback()
    if (mass.trim() === '' || length.trim() === '') {
      setError('Please provide both DNA template concentration and DNA length.')
      setResult(null)
      return
    }

    const massVal = Number(mass)
    const lengthVal = Number(length)

    if (Number.isNaN(massVal) || Number.isNaN(lengthVal) || massVal < 0 || lengthVal < 0) {
      setError('Please enter valid, non-negative numbers only.')
      setResult(null)
      return
    }

    if (massVal === 0 || lengthVal === 0) {
      setError('DNA concentration and DNA length must both be greater than zero.')
      setResult(null)
      return
    }

    const massNg = massToNanograms(massVal, massUnit)
    const copyNumber = calculateCopyNumber(massNg, lengthVal)

    if (!Number.isFinite(copyNumber)) {
      setError('Calculation produced an invalid result. Please verify your inputs.')
      setResult(null)
      return
    }

    setError(null)
    setResult(copyNumber)
  }

  const resultText = result !== null ? `DNA Copy Number = ${formatNumber(result, 2)} copies` : ''

  const handleCopy = async () => {
    if (!resultText)
      return
    try {
      await navigator.clipboard.writeText(resultText)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
    catch {
      setError('Unable to copy to clipboard.')
    }
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-slate-900 mb-1">qPCR DNA Copy Number Calculator</h2>
      <p className="text-sm text-slate-600 mb-4">
        Determine exact DNA copy number from template mass and fragment length for qPCR standard curve preparation.
      </p>

      <div className="flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50/70 p-4 mb-6">
        <Lightbulb className="h-5 w-5 text-emerald-700 flex-shrink-0 mt-0.5" aria-hidden="true" />
        <p className="text-sm text-slate-700 leading-relaxed">
          <span className="font-semibold text-slate-900">How to use this calculator: </span>
          Enter the mass of your DNA template (choose ng or pg) and the length of the amplicon or fragment in base pairs. Press <span className="font-semibold">Calculate</span> to instantly convert mass into an exact copy number for building qPCR standard curves.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <div>
          <label htmlFor="dna-mass" className="block text-sm font-semibold text-slate-800 mb-1.5">
            DNA Template Concentration
          </label>
          <div className="flex gap-2">
            <input
              id="dna-mass"
              type="number"
              inputMode="decimal"
              min="0"
              step="any"
              placeholder="e.g. 10"
              value={mass}
              onChange={e => { setMass(e.target.value); clearFeedback() }}
              aria-describedby="dna-mass-hint"
              className="flex-1 min-w-0 rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:border-emerald-600"
            />
            <select
              aria-label="DNA mass unit"
              value={massUnit}
              onChange={e => { setMassUnit(e.target.value as MassUnit); clearFeedback() }}
              className="rounded-md border border-slate-300 px-2 py-2 text-sm text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-600"
            >
              {MASS_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
            </select>
          </div>
          <p id="dna-mass-hint" className="mt-1.5 text-xs text-slate-500 leading-snug">
            The total mass of double-stranded DNA in your sample, typically measured by spectrophotometer or fluorometer.
          </p>
        </div>
        <div>
          <label htmlFor="dna-length" className="block text-sm font-semibold text-slate-800 mb-1.5">
            DNA Length (bp)
          </label>
          <input
            id="dna-length"
            type="number"
            inputMode="decimal"
            min="0"
            step="any"
            placeholder="e.g. 500"
            value={length}
            onChange={e => { setLength(e.target.value); clearFeedback() }}
            aria-describedby="dna-length-hint"
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:border-emerald-600"
          />
          <p id="dna-length-hint" className="mt-1.5 text-xs text-slate-500 leading-snug">
            The size of your amplicon or DNA fragment in base pairs (bp), as determined by primer design or sequencing.
          </p>
        </div>
      </div>

      <ValidationAlert message={error} />

      <div className="flex flex-wrap gap-3 mt-4 mb-4">
        <button
          type="button"
          onClick={handleCalculate}
          className="px-5 py-2.5 rounded-md text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2 transition-colors"
        >
          Calculate
        </button>
      </div>

      <div
        role="status"
        aria-live="polite"
        className="rounded-lg border border-emerald-200 bg-emerald-50 p-5 mb-4 min-h-[76px] flex flex-col justify-center"
      >
        {result !== null
          ? (
            <>
              <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700 mb-1">DNA Copy Number</p>
              <p className="text-2xl font-bold text-slate-900">{formatNumber(result, 2)} copies</p>
            </>
          )
          : (
            <p className="text-sm text-slate-500">Your calculated copy number will appear here.</p>
          )}
      </div>

      <CalculatorActions onReset={handleReset} onCopy={handleCopy} copied={copied} />

      <div className="mt-8 border-t border-slate-200 pt-6">
        <h3 className="text-lg font-bold text-slate-900 mb-2">How Avogadro's Number Applies to DNA Copies</h3>
        <p className="text-sm text-slate-600 leading-relaxed mb-3">
          Each base pair in double-stranded DNA has an average molecular weight of approximately 650 Daltons (g/mol). Multiplying the DNA length in base pairs by 650 gives the molecular weight of the full fragment in Daltons, which is numerically equivalent to grams per mole. To convert a mass of DNA into a number of molecules (copies), we divide the mass by the molecular weight to get moles, then multiply by Avogadro's number (6.022 × 10^23 molecules per mole).
        </p>
        <p className="text-sm text-slate-600 leading-relaxed">
          Because template mass is typically measured in nanograms, the formula rescales Avogadro's number by 10^-9 to give 6.022 × 10^14, so the working equation becomes Copy Number = (mass in ng × 6.022 × 10^14) / (length in bp × 650). This calculation is essential when preparing dilution series for qPCR standard curves, where accurate copy number input directly determines quantification precision.
        </p>

        <h4 className="text-base font-bold text-slate-900 mt-6 mb-3">Worked Examples</h4>
        <div className="space-y-4">
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <p className="text-sm font-semibold text-slate-900 mb-1">Example 1 — Plasmid standard for qPCR</p>
            <p className="text-sm text-slate-600 leading-relaxed">
              You have 10 ng of a 3,000 bp plasmid and need to know how many copies that represents.
              <br />
              Copy Number = (10 × 6.022 × 10^14) / (3000 × 650) = (6.022 × 10^15) / (1.95 × 10^6) = <span className="font-semibold text-slate-900">≈ 3.09 × 10^9 copies</span>. Use this value as the top point of your standard curve dilution series.
            </p>
          </div>

          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <p className="text-sm font-semibold text-slate-900 mb-1">Example 2 — Short PCR amplicon in picograms</p>
            <p className="text-sm text-slate-600 leading-relaxed">
              You measure 50 pg (0.05 ng) of a 150 bp amplicon.
              <br />
              Copy Number = (0.05 × 6.022 × 10^14) / (150 × 650) = (3.011 × 10^13) / (97,500) = <span className="font-semibold text-slate-900">≈ 3.09 × 10^8 copies</span>. Shorter amplicons yield more copies per unit mass because each molecule weighs less.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default QpcrCopyNumberCalculator
